import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'src/store';
import {
  CampaignCreateOption,
  CampaignCreateOptionKind,
} from '../api';

interface CampaignCreateOptionsState {
  items: CampaignCreateOption[];
  count: number;
  loading: boolean;
  error: string | null;
  lastFetchedCompanyId: string | null;
}

const initialState: CampaignCreateOptionsState = {
  items: [],
  count: 0,
  loading: false,
  error: null,
  lastFetchedCompanyId: null,
};

const campaignCreateOptionsSlice = createSlice({
  name: 'campaignCreateOptions',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCreateOptions: (
      state,
      action: PayloadAction<{ items: CampaignCreateOption[]; count: number }>,
    ) => {
      state.items = action.payload.items;
      state.count = action.payload.count;
    },
    setCreateOptionFavorite: (
      state,
      action: PayloadAction<{ kind: CampaignCreateOptionKind; id: string; favorite: boolean }>,
    ) => {
      const item = state.items.find(
        option => option.kind === action.payload.kind && option.id === action.payload.id,
      );
      if (item) {
        item.favorite = action.payload.favorite;
        if (item.config) item.config.favorite = action.payload.favorite;
        if (item.preset) item.preset.favorite = action.payload.favorite;
      }
    },
    setCreateOptionPresetInfo: (
      state,
      action: PayloadAction<{ id: string; name: string; description: string }>,
    ) => {
      const item = state.items.find(
        option => option.kind === 'preset' && option.id === action.payload.id,
      );
      if (item && item.preset) {
        item.preset.name = action.payload.name;
        item.preset.description = action.payload.description;
      }
    },
    removeCreateOption: (
      state,
      action: PayloadAction<{ kind: CampaignCreateOptionKind; id: string }>,
    ) => {
      state.items = state.items.filter(
        option => !(option.kind === action.payload.kind && option.id === action.payload.id),
      );
      state.count = state.items.length;
    },
    setLastFetchedCompanyId: (state, action: PayloadAction<string | null>) => {
      state.lastFetchedCompanyId = action.payload;
    },
  },
});

export const getCampaignCreateOptions = (state: RootState) => state.campaignCreateOptions.items;
export const getCampaignCreateOptionsLoading = (state: RootState) =>
  state.campaignCreateOptions.loading;
export const getCampaignCreateOptionsError = (state: RootState) =>
  state.campaignCreateOptions.error;
export const getCreateOptionsLastFetchedCompanyId = (state: RootState) =>
  state.campaignCreateOptions.lastFetchedCompanyId;

export const {
  setLoading,
  setError,
  setCreateOptions,
  setCreateOptionFavorite,
  setCreateOptionPresetInfo,
  removeCreateOption,
  setLastFetchedCompanyId,
} = campaignCreateOptionsSlice.actions;

export default campaignCreateOptionsSlice.reducer;
